import { z } from 'zod';
import type { PromptLlmModelProvider } from './prompt-llm-models.js';

// ─── LLM model list prices ──────────────────────────────────────────
// The providers' published rates, shipped with the code so a model the
// catalog starts returning is costed without anyone typing its prices in.
// Rates are micro-dollars per million tokens, the unit `LlmModelPrice` stores,
// so a seeded row and a hand-entered one are indistinguishable in the cost SQL.
//
// These are global-endpoint list prices. Tiered long-context rates (Claude
// Haiku 5.5 above 100K prompt tokens at 5x, Gemini Pro models above 200K input
// tokens) cannot be expressed by a single row, so those calls are costed at the
// standard tier and undercounted; the editor says so.

/** The providers usage rows are recorded under, which is what a price joins on. */
export const LlmListPriceProviderSchema = z.enum(['GEMINI', 'ANTHROPIC']);
export type LlmListPriceProvider = z.infer<typeof LlmListPriceProviderSchema>;

/** The prompt catalog names providers by vendor; usage rows and prices by API. */
const PRICE_PROVIDER_BY_CATALOG: Record<PromptLlmModelProvider, LlmListPriceProvider> = {
  anthropic: 'ANTHROPIC',
  google: 'GEMINI',
};

export function toListPriceProvider(provider: PromptLlmModelProvider): LlmListPriceProvider {
  return PRICE_PROVIDER_BY_CATALOG[provider];
}

/**
 * `updatedByName` on a row the seeder owns. Together with an empty
 * `updatedById` it is what marks a row as safe to refresh: anything an admin
 * saved carries their id and is never touched again.
 */
export const LLM_LIST_PRICE_UPDATED_BY = 'list price';

const VERTEX_PRICING_URL = 'https://cloud.google.com/vertex-ai/generative-ai/pricing';
/** Anthropic's page, which states Google Cloud's global endpoint bills these rates as-is. */
const ANTHROPIC_PRICING_URL = 'https://platform.claude.com/docs/en/about-claude/pricing';

const MICROS_PER_USD = 1_000_000;

/** Gemini 3.6–3.8 Flash list prices double from this instant (UTC). */
const GEMINI_FLASH_PRICE_CHANGE = '2027-01-01T00:00:00.000Z';

export interface LlmListPriceRates {
  inputPerMillion: number;
  outputPerMillion: number;
  thinkingPerMillion: number;
  cacheWritePerMillion: number;
  cacheReadPerMillion: number;
}

export interface LlmListPrice extends LlmListPriceRates {
  provider: LlmListPriceProvider;
  /** The unversioned id. Versioned ids such as `claude-opus-4-6@20260205` resolve to it. */
  model: string;
  /** Inclusive ISO instant. Absent means "since before Nexus called it". */
  effectiveFrom?: string;
  /** Exclusive ISO instant. Absent means "until replaced". */
  effectiveTo?: string;
  /** Where the rate was read, so a stale row can be checked. */
  sourceUrl: string;
}

interface UsdRates {
  input: number;
  output: number;
  thinking: number;
  cacheWrite: number;
  cacheRead: number;
}

function toMicros(usdPerMillion: number): number {
  return Math.round(usdPerMillion * MICROS_PER_USD);
}

function listPrice(
  provider: LlmListPriceProvider,
  model: string,
  usd: UsdRates,
  range: Pick<LlmListPrice, 'effectiveFrom' | 'effectiveTo'> = {},
  sourceUrl: string = VERTEX_PRICING_URL,
): LlmListPrice {
  return {
    provider,
    model,
    inputPerMillion: toMicros(usd.input),
    outputPerMillion: toMicros(usd.output),
    thinkingPerMillion: toMicros(usd.thinking),
    cacheWritePerMillion: toMicros(usd.cacheWrite),
    cacheReadPerMillion: toMicros(usd.cacheRead),
    ...range,
    sourceUrl,
  };
}

function claude(model: string, usd: UsdRates): LlmListPrice {
  return listPrice('ANTHROPIC', model, usd, {}, ANTHROPIC_PRICING_URL);
}

const CLAUDE_OPUS_5_5: UsdRates = { input: 4, output: 20, thinking: 20, cacheWrite: 5, cacheRead: 0.2 };
const CLAUDE_OPUS: UsdRates = { input: 5, output: 25, thinking: 25, cacheWrite: 6.25, cacheRead: 0.5 };
const CLAUDE_SONNET_5_5: UsdRates = { input: 2, output: 10, thinking: 10, cacheWrite: 2.5, cacheRead: 0.1 };
const CLAUDE_SONNET_5: UsdRates = { input: 2, output: 10, thinking: 10, cacheWrite: 2.5, cacheRead: 0.2 };
const CLAUDE_SONNET_4: UsdRates = { input: 3, output: 15, thinking: 15, cacheWrite: 3.75, cacheRead: 0.3 };
const CLAUDE_HAIKU_4_5: UsdRates = { input: 1, output: 5, thinking: 5, cacheWrite: 1.25, cacheRead: 0.1 };
/** The up-to-100K-token tier; longer prompts bill 5x and are undercounted here. */
const CLAUDE_HAIKU_5_5: UsdRates = { input: 0.1, output: 0.5, thinking: 0.5, cacheWrite: 0.125, cacheRead: 0.01 };

const GEMINI_FLASH_2026: UsdRates = { input: 0.75, output: 3.75, thinking: 3.75, cacheWrite: 0, cacheRead: 0.075 };
const GEMINI_FLASH_2027: UsdRates = { input: 1.5, output: 7.5, thinking: 7.5, cacheWrite: 0, cacheRead: 0.15 };

function geminiFlash(model: string): LlmListPrice[] {
  return [
    listPrice('GEMINI', model, GEMINI_FLASH_2026, { effectiveTo: GEMINI_FLASH_PRICE_CHANGE }),
    listPrice('GEMINI', model, GEMINI_FLASH_2027, { effectiveFrom: GEMINI_FLASH_PRICE_CHANGE }),
  ];
}

/**
 * One row per model and price period. Image models bill each generated image
 * as output tokens (1,120 per 1K image), which is where usage rows record
 * them, so their output rate is the per-image cost divided by that count.
 */
export const LLM_MODEL_LIST_PRICES: readonly LlmListPrice[] = [
  claude('claude-opus-5-5', CLAUDE_OPUS_5_5),
  claude('claude-opus-5', CLAUDE_OPUS),
  claude('claude-opus-4-8', CLAUDE_OPUS),
  claude('claude-opus-4-7', CLAUDE_OPUS),
  claude('claude-opus-4-6', CLAUDE_OPUS),
  claude('claude-sonnet-5-5', CLAUDE_SONNET_5_5),
  claude('claude-sonnet-5', CLAUDE_SONNET_5),
  claude('claude-sonnet-4-6', CLAUDE_SONNET_4),
  claude('claude-sonnet-4-5', CLAUDE_SONNET_4),
  claude('claude-haiku-5-5', CLAUDE_HAIKU_5_5),
  claude('claude-haiku-4-5', CLAUDE_HAIKU_4_5),

  ...geminiFlash('gemini-3.8-flash'),
  ...geminiFlash('gemini-3.7-flash'),
  ...geminiFlash('gemini-3.6-flash'),
  listPrice('GEMINI', 'gemini-3.5-flash', { input: 1.5, output: 9, thinking: 9, cacheWrite: 0, cacheRead: 0.15 }),
  listPrice('GEMINI', 'gemini-3.5-flash-lite', { input: 0.3, output: 2.5, thinking: 2.5, cacheWrite: 0, cacheRead: 0.03 }),
  listPrice('GEMINI', 'gemini-3.1-flash-lite', { input: 0.25, output: 1.5, thinking: 1.5, cacheWrite: 0, cacheRead: 0.025 }),
  listPrice('GEMINI', 'gemini-3.1-pro-preview', { input: 2, output: 12, thinking: 12, cacheWrite: 0, cacheRead: 0.2 }),
  listPrice('GEMINI', 'gemini-2.5-pro', { input: 1.25, output: 10, thinking: 10, cacheWrite: 0, cacheRead: 0.125 }),
  listPrice('GEMINI', 'gemini-2.5-flash', { input: 0.3, output: 2.5, thinking: 2.5, cacheWrite: 0, cacheRead: 0.03 }),
  listPrice('GEMINI', 'gemini-2.5-flash-lite', { input: 0.1, output: 0.4, thinking: 0.4, cacheWrite: 0, cacheRead: 0.01 }),
  listPrice('GEMINI', 'gemini-2.0-flash', { input: 0.15, output: 0.6, thinking: 0.6, cacheWrite: 0, cacheRead: 0.0375 }),

  // Image models bill image output and text output at different rates, but
  // usage rows record both as output tokens, so output carries the image rate
  // and the rare text response is overcounted (10x on 3 Pro Image, 20x on the
  // Flash Image models). Thinking carries the text output rate.
  listPrice('GEMINI', 'gemini-3-pro-image', { input: 2, output: 120, thinking: 12, cacheWrite: 0, cacheRead: 0.2 }),
  listPrice('GEMINI', 'gemini-3.1-flash-image', { input: 0.5, output: 60, thinking: 3, cacheWrite: 0, cacheRead: 0.05 }),
  listPrice('GEMINI', 'gemini-3.1-flash-lite-image', { input: 0.25, output: 30, thinking: 1.5, cacheWrite: 0, cacheRead: 0.025 }),
  listPrice('GEMINI', 'gemini-nano-banana-2.1', { input: 1.5, output: 30, thinking: 7.5, cacheWrite: 0, cacheRead: 0.15 }),
];

/**
 * A version pin on an otherwise listed model: Vertex's `@YYYYMMDD`, Gemini's
 * `-001`, or Anthropic's `-YYYYMMDD`. Anchored to these shapes on purpose — a
 * bare prefix match would price `gemini-3.1-flash-lite-image` as
 * `gemini-3.1-flash-lite`.
 */
const VERSION_SUFFIX = /(?:@[^@]+|-\d{3}|-\d{8})$/;

function isEffectiveAt(price: LlmListPrice, at: Date): boolean {
  const time = at.getTime();
  if (price.effectiveFrom && time < Date.parse(price.effectiveFrom)) return false;
  if (price.effectiveTo && time >= Date.parse(price.effectiveTo)) return false;
  return true;
}

/** The list price for a model at a moment, or null when Nexus ships none. */
export function listPriceFor(
  provider: LlmListPriceProvider,
  model: string,
  at: Date = new Date(),
): LlmListPrice | null {
  const id = model.trim().toLowerCase();
  const candidates = [id, id.replace(VERSION_SUFFIX, '')];

  for (const candidate of candidates) {
    const match = LLM_MODEL_LIST_PRICES.find(
      (price) => price.provider === provider && price.model === candidate && isEffectiveAt(price, at),
    );
    if (match) return match;
  }
  return null;
}

/** Just the five rates, for comparing a stored row against the list. */
export function toListPriceRates(price: LlmListPriceRates): LlmListPriceRates {
  return {
    inputPerMillion: price.inputPerMillion,
    outputPerMillion: price.outputPerMillion,
    thinkingPerMillion: price.thinkingPerMillion,
    cacheWritePerMillion: price.cacheWritePerMillion,
    cacheReadPerMillion: price.cacheReadPerMillion,
  };
}
